<template>
  <div class="batch-receive-container">
    <!-- 页面头部 -->
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">批量收货</h1>
    </header>

    <!-- 状态提示弹窗 -->
    <div v-if="statusMsg" class="toast-overlay">
      <div class="toast-box" :class="statusType">
        <span class="toast-icon">{{ getToastIcon(statusType) }}</span>
        <span class="toast-text">{{ statusMsg }}</span>
      </div>
    </div>

    <main class="content">
      <!-- 供应商信息 -->
      <div v-if="selectedList[0]?.supplierName" class="supplier-section">
        <div class="supplier-card">
          <span class="supplier-label">供应商：</span>
          <span class="supplier-name">{{ selectedList[0].supplierName }}</span>
        </div>
      </div>

      <!-- 单据列表 -->
      <div class="orders-section">
        <h3 class="section-title">待收货单据（{{ selectedList.length }}）</h3>
        <div class="orders-list">
          <div
            v-for="(order, index) in selectedList"
            :key="order.id || order.wareInId || index"
            class="order-item"
          >
            <div class="order-header">
              <span class="order-no">{{
                order.receiptNo || order.wareInNo || "-"
              }}</span>
            </div>
            <div class="order-info">
              <div class="info-row">
                <span class="info-label">物料编码：</span>
                <span class="info-value">{{
                  getMaterialNoDisplay(order)
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">待收数量：</span>
                <span class="info-value text-primary">{{
                  formatNumber(getTotalWareInNum(order))
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">已收数量：</span>
                <span class="info-value text-success">{{
                  formatNumber(getCollectedNum(order))
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">剩余数量：</span>
                <span class="info-value text-warning">{{
                  formatNumber(getRemainingNum(order))
                }}</span>
              </div>
              <div class="info-row qty-input-row">
                <span class="info-label">归托数量：</span>
                <input
                  :value="getOrderQty(order)"
                  type="number"
                  step="1"
                  min="0"
                  :max="getRemainingNum(order)"
                  class="order-qty-input"
                  placeholder="输入数量"
                  @input="handleQtyInput(order, $event)"
                />
              </div>
            </div>
          </div>
          <div class="scroll-bottom-tip">我也是有底线的～</div>
        </div>
      </div>

      <!-- 归托区域 -->
      <div class="collect-section">
        <h3 class="section-title">托盘信息</h3>
        <div class="collect-card">
          <!-- 托盘码输入 -->
          <div class="input-group">
            <label class="input-label">托盘码</label>
            <div class="input-with-btn">
              <div class="input-wrapper">
                <input
                  ref="palletInput"
                  v-model="palletCode"
                  placeholder="请扫描或输入托盘码"
                  @keyup.enter="handlePalletEnter"
                  class="pallet-input"
                  type="text"
                />
                <button
                  v-if="palletCode"
                  @click="clearPalletCode"
                  class="clear-btn"
                >
                  ×
                </button>
                <button @click="startScan" class="scan-btn" aria-label="扫码">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    width="20"
                    height="20"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <path
                      d="M7 7h3v3H7zM14 7h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z"
                    />
                  </svg>
                </button>
              </div>
              <button
                @click="addPalletToAll"
                class="btn primary-btn inline-btn"
                :disabled="!palletCode || !hasOrderQty"
              >
                归托
              </button>
            </div>
          </div>

          <!-- 已添加的托盘列表 -->
          <div v-if="hasAnyPallets" class="pallet-list">
            <h4 class="list-title">已归托记录</h4>
            <div
              v-for="(pallets, wareInId) in allPalletsMap"
              :key="wareInId"
              class="pallet-group"
              v-show="getOrderNoById(wareInId)"
            >
              <div class="pallet-group-header">
                <span class="pallet-group-title">{{
                  getOrderNoById(wareInId)
                }}</span>
                <span class="pallet-group-material">{{
                  getMaterialNoById(wareInId)
                }}</span>
                <button
                  @click="clearOrderPallets(wareInId)"
                  class="clear-all-btn"
                >
                  清空
                </button>
              </div>
              <div
                v-for="(item, idx) in pallets"
                :key="idx"
                class="pallet-item"
              >
                <div class="pallet-info">
                  <span class="pallet-code">{{ item.palletCode }}</span>
                  <span class="pallet-qty">{{
                    formatNumber(item.wareInNum)
                  }}</span>
                </div>
                <button
                  @click="removePalletItem(wareInId, idx)"
                  class="remove-btn"
                >
                  删除
                </button>
              </div>
            </div>
            <div class="scroll-bottom-tip">我也是有底线的～</div>
          </div>
        </div>
      </div>
    </main>

    <!-- 固定底部：确认收货按钮 -->
    <div class="fixed-bottom-action">
      <button
        @click="handleBatchConfirm"
        class="btn confirm-btn"
        :disabled="isSubmitting || !hasAnyPallets"
      >
        {{ isSubmitting ? "提交中..." : "批量确认收货" }}
      </button>
    </div>

    <!-- 加载遮罩 -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-box">
        <div class="loading-spinner"></div>
        <div class="loading-text">加载中...</div>
      </div>
    </div>

    <!-- 确认弹窗 -->
    <div v-if="showConfirmModal" class="modal-overlay">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">确认收货</span>
        </div>
        <div class="modal-body">
          <p class="modal-desc">确认要批量收货以下单据吗？</p>
          <div class="modal-group-list">
            <div
              v-for="(group, groupIdx) in groupedConfirmData"
              :key="groupIdx"
              class="modal-group-item"
            >
              <div class="modal-group-header">
                <span class="modal-group-material">{{ group.materialNo }}</span>
                <span class="modal-group-total"
                  >合计：{{ formatNumber(group.totalQty) }}</span
                >
              </div>
              <div class="modal-group-orders">
                <div
                  v-for="(order, orderIdx) in group.orders"
                  :key="orderIdx"
                  class="modal-group-order"
                >
                  <div class="modal-group-order-header">
                    <span class="modal-group-order-no">{{
                      order.orderNo
                    }}</span>
                    <span class="modal-group-order-qty">{{
                      formatNumber(order.totalQty)
                    }}</span>
                  </div>
                  <div class="modal-group-pallets">
                    <span
                      v-for="(p, idx) in order.pallets"
                      :key="idx"
                      class="modal-group-pallet-tag"
                    >
                      {{ p.palletCode }} ({{ formatNumber(p.wareInNum) }})
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showConfirmModal = false" class="btn cancel-btn">
            取消
          </button>
          <button @click="confirmSubmit" class="btn confirm-modal-btn">
            确认收货
          </button>
        </div>
      </div>
    </div>

    <!-- 提交二次确认弹窗 -->
    <div v-if="showSubmitConfirmModal" class="modal-overlay">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">确认提交</span>
        </div>
        <div class="modal-body">
          <p class="modal-desc">确认要提交收货吗？</p>
          <p class="modal-desc">提交后将完成所有选中单据的入库操作。</p>
        </div>
        <div class="modal-footer">
          <button
            @click="showSubmitConfirmModal = false"
            class="btn cancel-btn"
          >
            取消
          </button>
          <button @click="handleSubmitConfirm" class="btn confirm-modal-btn">
            确认提交
          </button>
        </div>
      </div>
    </div>

    <!-- 扫码组件 -->
    <CameraScan
      v-model:visible="scanVisible"
      title="扫描托盘码"
      @success="handleScanSuccess"
      @close="scanVisible = false"
    />
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, nextTick, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import request from "@/utils/request";
import CameraScan from "@/components/CameraScan";

export default {
  name: "WareInBatchReceive",
  components: {
    CameraScan,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    // 状态变量
    const selectedList = ref([]);
    const allPalletsMap = reactive({}); // { [wareInId]: [{ palletCode, wareInNum }] }
    const orderQtyMap = reactive({}); // { [wareInId]: qty }
    const palletCode = ref("");
    const scanVisible = ref(false);
    const isLoading = ref(false);
    const isSubmitting = ref(false);
    const statusMsg = ref("");
    const statusType = ref("info");
    const palletInput = ref(null);
    const showConfirmModal = ref(false);
    const showSubmitConfirmModal = ref(false);

    // 计算属性：是否有任何归托记录
    const hasAnyPallets = computed(() => {
      return Object.values(allPalletsMap).some((pallets) => pallets.length > 0);
    });

    // 计算属性：是否有订单有输入数量
    const hasOrderQty = computed(() => {
      if (Object.values(orderQtyMap).some((qty) => qty > 0)) {
        return true;
      }
      return selectedList.value.some((order) => {
        const remaining = getRemainingNum(order);
        return remaining > 0;
      });
    });

    // 计算属性：按物料编码分组的确认收货数据
    const groupedConfirmData = computed(() => {
      const grouped = {};
      for (const [wareInId, pallets] of Object.entries(allPalletsMap)) {
        if (!pallets || pallets.length === 0) continue;

        const materialNo = getMaterialNoById(wareInId);
        const orderNo = getOrderNoById(wareInId);

        if (!grouped[materialNo]) {
          grouped[materialNo] = {
            materialNo,
            totalQty: 0,
            orders: [],
          };
        }

        const orderItem = {
          orderNo,
          wareInId,
          pallets: [...pallets],
          totalQty: pallets.reduce((sum, p) => sum + Number(p.wareInNum), 0),
        };

        grouped[materialNo].totalQty += orderItem.totalQty;
        grouped[materialNo].orders.push(orderItem);
      }
      return Object.values(grouped);
    });

    // 处理返回：从哪里来回哪里去，避免误回 PLS
    const handleBack = () => {
      const backPath = route.query?.back;
      if (backPath) {
        router.replace(String(backPath));
        return;
      }
      const source = route.query?.source || "wareIn";
      router.replace(
        source === "pls" ? "/pls-receive" : "/warein-receive?status=pending",
      );
    };

    // 显示状态信息
    const showStatus = (msg, type = "info") => {
      statusMsg.value = msg;
      statusType.value = type;
      setTimeout(() => {
        statusMsg.value = "";
      }, 2000);
    };

    // 获取弹窗图标
    const getToastIcon = (type) => {
      switch (type) {
        case "success":
          return "✓";
        case "error":
          return "✗";
        case "warning":
          return "!";
        default:
          return "i";
      }
    };

    // 格式化数字
    const formatNumber = (num) => {
      if (num == null) return "0";
      return Number(num).toFixed(3);
    };

    // 获取物料编码显示
    const getMaterialNoDisplay = (order) => {
      if (order.materialNo) return order.materialNo;
      const details = order.wareInDetailList || [];
      const materialNos = [
        ...new Set(details.map((d) => d.materialNo).filter(Boolean)),
      ];
      return materialNos.length > 0 ? materialNos.join(", ") : "-";
    };

    // 根据wareInId获取物料编码
    const getMaterialNoById = (wareInId) => {
      const order = selectedList.value.find(
        (o) =>
          String(o.id) === wareInId ||
          String(o.wareInId) === wareInId ||
          String(o.receiptNo) === wareInId ||
          String(o.wareInNo) === wareInId,
      );
      return order ? getMaterialNoDisplay(order) : "-";
    };

    // 获取单据唯一标识符
    const getUniqueId = (order) => {
      if (order.id) return String(order.id);
      if (order.wareInId) return String(order.wareInId);
      if (order.receiptNo) return String(order.receiptNo);
      if (order.wareInNo) return String(order.wareInNo);
      return String(Math.random());
    };

    // 获取总入库数量
    const getTotalWareInNum = (order) => {
      if (order.wareInNum) return Number(order.wareInNum);
      if (order.totalNum) return Number(order.totalNum);
      if (order.demandNum) return Number(order.demandNum);
      if (order.receiveNum) return Number(order.receiveNum);
      if (order.qty) return Number(order.qty);
      const details = order.wareInDetailList || order.details || [];
      if (details.length > 0) {
        return details.reduce(
          (sum, d) =>
            sum + (Number(d.wareInNum) || Number(d.num) || Number(d.qty) || 0),
          0,
        );
      }
      return 0;
    };

    // 获取状态文本
    const getOrderStatusText = (status) => {
      switch (status) {
        case 0:
          return "待分配";
        case 1:
          return "待收货";
        case 3:
          return "已完成";
        case 6:
          return "正在入库中";
        default:
          return "异常";
      }
    };

    // 获取状态样式
    const getOrderStatusClass = (status) => {
      switch (status) {
        case 0:
          return "status-pending";
        case 1:
          return "status-receiving";
        case 3:
          return "status-completed";
        case 6:
          return "status-processing";
        default:
          return "status-error";
      }
    };

    // 通过 wareInId 获取单据号
    const getOrderNoById = (wareInId) => {
      const targetId = String(wareInId);
      const order = selectedList.value.find(
        (o) => String(o.wareInId) === targetId || String(o.id) === targetId,
      );
      return order ? order.receiptNo || order.wareInNo || "" : "";
    };

    // 获取当前单据已收集数量
    const getCollectedNum = (order) => {
      const wareInId = getUniqueId(order);
      const pallets = allPalletsMap[wareInId] || [];
      const collected = pallets.reduce(
        (sum, p) => sum + Number(p.wareInNum),
        0,
      );
      return collected;
    };

    // 获取剩余数量
    const getRemainingNum = (order) => {
      const total = getTotalWareInNum(order);
      const collected = getCollectedNum(order);
      const remaining = total - collected;
      return remaining > 0 ? remaining : 0;
    };

    // 根据 wareInId 获取已入库数量
    const getCollectedNumByWareInId = (wareInId) => {
      const order = selectedList.value.find(
        (o) => String(o.wareInId) === wareInId || String(o.id) === wareInId,
      );
      if (order) {
        return getCollectedNum(order);
      }
      const pallets = allPalletsMap[wareInId] || [];
      return pallets.reduce((sum, p) => sum + Number(p.wareInNum), 0);
    };

    // 根据 wareInId 获取剩余数量
    const getRemainingByWareInId = (wareInId) => {
      const order = selectedList.value.find(
        (o) => String(o.wareInId) === wareInId || String(o.id) === wareInId,
      );
      if (order) {
        return getRemainingNum(order);
      }
      return 0;
    };

    // 获取归托数量（确保返回正确的初始值）
    const getOrderQty = (order) => {
      const wareInId = getUniqueId(order);
      const qty = orderQtyMap[wareInId];
      if (qty === undefined || qty === null) {
        const remaining = getRemainingNum(order);
        orderQtyMap[wareInId] = remaining > 0 ? remaining : 0;
        return orderQtyMap[wareInId];
      }
      return qty;
    };

    // 获取归托数量的默认显示值（处理 0 的情况）
    const getDefaultOrderQty = (order) => {
      const wareInId = getUniqueId(order);
      const remaining = getRemainingNum(order);
      const qty = orderQtyMap[wareInId];
      if (qty === undefined || qty === null) {
        orderQtyMap[wareInId] = remaining;
        return remaining;
      }
      return qty;
    };

    // 处理数量输入
    const handleQtyInput = (order, event) => {
      const wareInId = getUniqueId(order);
      const value = Number(event.target.value);
      const maxQty = getRemainingNum(order);

      if (isNaN(value) || value < 0) {
        orderQtyMap[wareInId] = 0;
        event.target.value = 0;
      } else if (value > maxQty) {
        orderQtyMap[wareInId] = maxQty;
        event.target.value = maxQty;
      } else {
        orderQtyMap[wareInId] = value;
      }
    };

    // 处理托盘码回车
    const handlePalletEnter = () => {
      if (palletCode.value.trim() && hasOrderQty.value) {
        addPalletToAll();
      }
    };

    // 清空托盘码
    const clearPalletCode = () => {
      palletCode.value = "";
      nextTick(() => {
        if (palletInput.value) {
          palletInput.value.focus();
        }
      });
    };

    const startScan = () => {
      scanVisible.value = true;
    };

    const handleScanSuccess = (decodedText) => {
      const text = String(decodedText || "")
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .replace(/[\r\n\t]/g, "")
        .trim();

      if (!text) return;

      palletCode.value = text;
      scanVisible.value = false;
    };

    // 批量添加托盘到所有有数量的订单
    const addPalletToAll = () => {
      if (!palletCode.value.trim()) {
        showStatus("请输入托盘码", "error");
        return;
      }

      let addedCount = 0;
      selectedList.value.forEach((order) => {
        const wareInId = getUniqueId(order);
        const qty = getOrderQty(order);

        if (qty <= 0) {
          return;
        }

        if (!allPalletsMap[wareInId]) {
          allPalletsMap[wareInId] = [];
        }
        allPalletsMap[wareInId].push({
          palletCode: palletCode.value.trim(),
          wareInNum: Number(qty),
        });

        orderQtyMap[wareInId] = getRemainingNum(order);
        addedCount++;
      });

      if (addedCount > 0) {
        showStatus(`成功添加 ${addedCount} 个托盘记录`, "success");
        palletCode.value = "";
        nextTick(() => {
          if (palletInput.value) {
            palletInput.value.focus();
          }
        });
      } else {
        showStatus("没有可归托的单据", "warning");
      }
    };

    // 删除托盘记录
    const removePalletItem = (wareInId, index) => {
      if (allPalletsMap[wareInId]) {
        allPalletsMap[wareInId].splice(index, 1);
        const order = selectedList.value.find(
          (o) => String(o.wareInId) === wareInId || String(o.id) === wareInId,
        );
        if (order) {
          orderQtyMap[wareInId] = getRemainingNum(order);
        }
      }
    };

    const clearOrderPallets = (wareInId) => {
      allPalletsMap[wareInId] = [];
      const order = selectedList.value.find(
        (o) => String(o.wareInId) === wareInId || String(o.id) === wareInId,
      );
      if (order) {
        orderQtyMap[wareInId] = getRemainingNum(order);
      }
    };

    // 批量确认收货 - 显示确认弹窗
    const handleBatchConfirm = () => {
      if (!hasAnyPallets.value) {
        showStatus("请先添加托盘记录", "error");
        return;
      }

      let totalWareIn = 0;
      let totalCollected = 0;
      let hasRemaining = false;

      selectedList.value.forEach((order) => {
        const orderTotal = Number(order.wareInNum) || 0;
        const orderCollected = getCollectedNum(order);
        totalWareIn += orderTotal;
        totalCollected += orderCollected;
        if (orderCollected < orderTotal) {
          hasRemaining = true;
        }
      });

      if (!hasRemaining && totalCollected === totalWareIn && totalWareIn > 0) {
        showSubmitConfirmModal.value = true;
      } else {
        showConfirmModal.value = true;
      }
    };

    // 确认提交（经过二次确认后）
    const handleSubmitConfirm = () => {
      showSubmitConfirmModal.value = false;
      confirmSubmit();
    };

    // 确认提交
    const confirmSubmit = async () => {
      showConfirmModal.value = false;

      // 构建请求参数
      const params = [];
      for (const [wareInId, pallets] of Object.entries(allPalletsMap)) {
        if (pallets.length === 0) continue;

        // 找到对应的订单信息 - 使用 getUniqueId 来匹配
        const order = selectedList.value.find(
          (o) => getUniqueId(o) === wareInId,
        );

        if (order) {
          // 获取 wareInId，优先使用有效的 id 或 wareInId
          let submitWareInId = null;
          if (order.id !== null && order.id !== undefined) {
            submitWareInId = String(order.id);
          }
          if (
            !submitWareInId &&
            order.wareInId !== null &&
            order.wareInId !== undefined
          ) {
            submitWareInId = String(order.wareInId);
          }
          if (!submitWareInId) {
            submitWareInId = wareInId;
          }

          // 获取物料ID，从多个位置尝试获取
          let materialId = order.materialId || order.material_id || null;

          // 如果顶层没有，从详情列表中获取第一个物料的ID
          if (!materialId) {
            const details =
              order.wareInDetailList ||
              order.details ||
              order.materialList ||
              [];
            if (details.length > 0) {
              materialId =
                details[0].materialId || details[0].material_id || null;
            }
          }

          params.push({
            wareInId: submitWareInId,
            materialId: materialId,
            type: 0,
            wareInDetailRecordVOList: pallets.map((p) => ({
              palletCode: p.palletCode,
              wareInNum: p.wareInNum,
            })),
          });
        }
      }

      if (params.length === 0) {
        showStatus("没有有效的收货数据", "error");
        return;
      }

      isSubmitting.value = true;
      showStatus("正在提交...", "info");

      try {
        const response = await request.post(
          "/uic-system/api_hf/warein/batchCollectAndConfirm",
          params,
        );

        if (response.code === 200) {
          showStatus("批量收货成功", "success");
          // 延迟返回，根据来源决定返回路径
          setTimeout(() => {
            const source = route.query.source || "wareIn";
            const backPath =
              route.query.back ||
              (source === "pls" ? "/pls-receive" : "/warein-receive");
            router.replace(String(backPath));
          }, 1500);
        } else {
          showStatus(response.message || "收货失败", "error");
        }
      } catch (error) {
        console.error("批量收货失败:", error);
        showStatus(error.message || "网络异常，请稍后重试", "error");
      } finally {
        isSubmitting.value = false;
      }
    };

    // 监听 selectedList 变化，自动填充归托数量
    watch(
      () => selectedList.value,
      async (newList) => {
        if (newList && newList.length > 0) {
          await nextTick();
          newList.forEach((order) => {
            const wareInId = getUniqueId(order);
            const total = getTotalWareInNum(order);
            const collected = getCollectedNum(order);
            const remaining = total - collected;
            orderQtyMap[wareInId] = remaining > 0 ? remaining : 0;
          });
        }
      },
      { deep: true },
    );

    // 初始化
    onMounted(() => {
      const selectedListStr = route.query.selectedList;
      if (selectedListStr) {
        try {
          selectedList.value = JSON.parse(selectedListStr);
          // 直接初始化归托数量和已归托记录
          nextTick(() => {
            selectedList.value.forEach((order) => {
              const wareInId = getUniqueId(order);

              // 如果有已归托记录列表，先添加到 allPalletsMap
              const recordList =
                order.wareInDetailRecordVOList ||
                order.records ||
                order.historyRecords ||
                [];
              if (Array.isArray(recordList) && recordList.length > 0) {
                recordList.forEach((record) => {
                  if (!allPalletsMap[wareInId]) {
                    allPalletsMap[wareInId] = [];
                  }
                  allPalletsMap[wareInId].push({
                    palletCode: record.palletCode || record.code || "",
                    wareInNum:
                      Number(record.wareInNum) ||
                      Number(record.num) ||
                      Number(record.qty) ||
                      0,
                  });
                });
              }

              const total = getTotalWareInNum(order);
              const collected = getCollectedNum(order);
              const remaining = total - collected;
              orderQtyMap[wareInId] = remaining > 0 ? remaining : 0;
            });
          });
        } catch (e) {
          console.error("解析参数失败:", e);
          showStatus("参数解析失败", "error");
        }
      } else {
        showStatus("没有选择单据", "error");
      }

      // 自动聚焦托盘码输入框
      nextTick(() => {
        if (palletInput.value) {
          palletInput.value.focus();
        }
      });
    });

    return {
      selectedList,
      allPalletsMap,
      orderQtyMap,
      palletCode,
      hasAnyPallets,
      hasOrderQty,
      groupedConfirmData,
      isLoading,
      isSubmitting,
      statusMsg,
      statusType,
      palletInput,
      handleBack,
      showStatus,
      getToastIcon,
      formatNumber,
      getMaterialNoDisplay,
      getMaterialNoById,
      getTotalWareInNum,
      getOrderStatusText,
      getOrderStatusClass,
      getOrderNoById,
      getCollectedNum,
      getRemainingNum,
      getCollectedNumByWareInId,
      getRemainingByWareInId,
      getOrderQty,
      getDefaultOrderQty,
      handleQtyInput,
      handlePalletEnter,
      clearPalletCode,
      scanVisible,
      startScan,
      handleScanSuccess,
      addPalletToAll,
      removePalletItem,
      clearOrderPallets,
      handleBatchConfirm,
      handleSubmitConfirm,
      confirmSubmit,
      showConfirmModal,
      showSubmitConfirmModal,
    };
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.batch-receive-container {
  min-height: 100vh;
  background-color: var(--background-color);
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding-bottom: 70px;
}

/* 头部 */
.header {
  display: flex;
  align-items: center;
  padding: 0 12px;
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 64px;
}

.back-btn {
  background: transparent;
  border: none;
  width: 2.2rem;
  height: 2.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 0.4rem;
}
.back-btn:active {
  background-color: var(--card-background);
}
.icon-back {
  width: 1.2rem;
  height: 1.2rem;
}

.title {
  flex: 1;
  text-align: center;
  font-size: 1rem;
  font-weight: 600;
  color: var(--header-text);
}

/* 状态弹窗 */
.toast-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.toast-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.2rem;
  background: rgba(0, 0, 0, 0.85);
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  animation: toast-fade-in 0.3s ease;
}

@keyframes toast-fade-in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.toast-icon {
  font-size: 1.2rem;
  font-weight: bold;
}

.toast-text {
  font-size: 0.9rem;
  color: white;
}

.toast-box.success .toast-icon {
  color: #10b981;
}

.toast-box.error .toast-icon {
  color: #ef4444;
}

.toast-box.warning .toast-icon {
  color: #f59e0b;
}

.toast-box.info .toast-icon {
  color: #3b82f6;
}

/* 内容区域 */
.content {
  flex: 1;
  padding: 8px 6px;
  margin-top: calc(64px + env(safe-area-inset-top));
}

/* 供应商信息 */
.supplier-section {
  margin-bottom: 12px;
}

.supplier-card {
  background: var(--card-background);
  border-radius: 16px;
  padding: 12px 14px;
  box-shadow: 0 4px 14px var(--shadow-light);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
}

.supplier-label {
  font-size: 13px;
  color: var(--text-secondary);
  margin-right: 6px;
}

.supplier-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

/* 卡片标题 */
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 10px 0 8px 4px;
}

/* 单据列表 */
.orders-section {
  margin-bottom: 12px;
}
.orders-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 310px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 8px 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.5);
}
.orders-list::-webkit-scrollbar {
  width: 4px;
}
.orders-list::-webkit-scrollbar-track {
  background: transparent;
}
.orders-list::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 2px;
}
.order-item {
  background: var(--card-background);
  border-radius: 16px;
  padding: 12px 14px 10px;
  box-shadow: 0 4px 14px var(--shadow-light);
  border: 1px solid var(--border-color);
}
.order-header {
  margin-bottom: 8px;
}
.order-no {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  font-family: monospace;
}
.order-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 12px;
}
.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}
.info-label {
  color: var(--text-secondary);
}
.info-value {
  color: var(--text-primary);
  font-weight: 500;
}
.text-primary {
  color: var(--primary-color);
}
.text-success {
  color: var(--success-color);
}
.text-warning {
  color: var(--warning-color);
}
.text-danger {
  color: var(--danger-color);
}
.qty-input-row {
  grid-column: 1 / -1;
  margin-top: 6px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.order-qty-input {
  width: 90px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 14px;
  text-align: right;
  outline: none;
  background-color: var(--background-color);
  color: var(--text-primary);
  transition: all 0.2s;
}
.order-qty-input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(35, 177, 192, 0.1);
}

/* 归托区域 */
.collect-section {
  margin-bottom: 12px;
}
.collect-card {
  background: var(--card-background);
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 4px 14px var(--shadow-light);
  border: 1px solid var(--border-color);
}
.input-group {
  margin-bottom: 0.8rem;
}
.input-label {
  display: block;
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-bottom: 0.3rem;
}
.input-wrapper {
  position: relative;
  flex: 8.5;
}

.input-with-btn {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.inline-btn {
  flex: 1.5;
  margin: 0;
}
.pallet-input,
.qty-input {
  width: 100%;
  padding: 0.7rem;
  border: 2px solid var(--border-color);
  border-radius: 0.5rem;
  font-size: 0.95rem;
  outline: none;
  background-color: var(--background-color);
  color: var(--text-primary);
}
.pallet-input:focus,
.qty-input:focus {
  border-color: var(--primary-color);
}
.clear-btn {
  position: absolute;
  right: 2.5rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  font-size: 1.2rem;
  color: var(--text-secondary);
  cursor: pointer;
}

.scan-btn {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  cursor: pointer;
  color: var(--primary-color);
  padding: 0;
}
.button-group {
  margin-top: 0.5rem;
  margin-bottom: 1rem;
}

/* 按钮 */
.btn {
  padding: 0.7rem 1.2rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s;
}
.btn:active:not(:disabled) {
  transform: scale(0.98);
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.primary-btn {
  background: var(--primary-color);
  color: var(--primary-text);
}
.delete-btn {
  padding: 0.3rem 0.6rem;
  font-size: 0.75rem;
  background: var(--error-color);
  color: white;
  border-radius: 0.3rem;
  border: none;
  cursor: pointer;
}
.clear-all-btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0.25rem;
  cursor: pointer;
}

/* 托盘列表 */
.pallet-list {
  margin-top: 16px;
  padding: 10px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  max-height: 220px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: rgba(255, 255, 255, 0.5);
}
.pallet-list::-webkit-scrollbar {
  width: 4px;
}
.pallet-list::-webkit-scrollbar-track {
  background: transparent;
}
.pallet-list::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 2px;
}
.scroll-bottom-tip {
  text-align: center;
  padding: 16px 0;
  font-size: 13px;
  color: red;
  font-weight: 700;
  opacity: 0.6;
}
.list-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
}
.pallet-group {
  margin-bottom: 0.8rem;
}
.pallet-group:last-child {
  margin-bottom: 0;
}
.pallet-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
  flex-wrap: wrap;
  gap: 0.3rem;
}
.pallet-group-title {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-primary);
}
.pallet-group-material {
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-left: 0.5rem;
}
.pallet-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 6px;
  border-bottom: 1px solid var(--border-color);
}
.pallet-item:last-child {
  border-bottom: none;
}
.pallet-info {
  display: flex;
  gap: 1rem;
}
.pallet-code {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-primary);
}
.pallet-qty {
  font-size: 0.9rem;
  color: var(--primary-color);
  font-weight: 600;
}
.remove-btn {
  padding: 8px 20px;
  font-size: 13px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 60px;
  text-align: center;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}
.remove-btn:active {
  transform: scale(0.95);
  opacity: 0.8;
}

/* 固定底部操作 */
.fixed-bottom-action {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--card-background);
  padding: 0.7rem 1rem;
  box-shadow: 0 -2px 10px var(--shadow-light);
  z-index: 999;
}
.confirm-btn {
  width: 100%;
  background: var(--success-color);
  color: var(--primary-text);
  padding: 0.9rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 0.6rem;
}

/* 加载 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.loading-box {
  background: var(--card-background);
  padding: 1.5rem;
  border-radius: 0.8rem;
  text-align: center;
}
.loading-spinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid var(--border-color);
  border-top: 3px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 0.8rem;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.loading-text {
  font-size: 0.8rem;
  color: var(--text-secondary);
}

/* 确认弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.modal-box {
  background: var(--card-background);
  border-radius: 0.8rem;
  width: 85%;
  max-width: 20rem;
  overflow: hidden;
  animation: modal-fade-in 0.2s ease;
}

@keyframes modal-fade-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-header {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  text-align: center;
}

.modal-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.modal-body {
  padding: 1rem;
}

.modal-desc {
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-bottom: 0.8rem;
  text-align: center;
}

.modal-order-list {
  background: var(--background-color);
  border-radius: 0.5rem;
  padding: 0.5rem;
}

.modal-group-list {
  background: var(--background-color);
  border-radius: 0.5rem;
  padding: 0.5rem;
}

.modal-group-item {
  margin-bottom: 0.6rem;
}

.modal-group-item:last-child {
  margin-bottom: 0;
}

.modal-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0.5rem;
  background: var(--primary-light);
  border-radius: 0.3rem;
  margin-bottom: 0.3rem;
}

.modal-group-material {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary-color);
}

.modal-group-total {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-primary);
}

.modal-group-orders {
  padding-left: 0.5rem;
}

.modal-group-order {
  padding: 0.4rem 0;
  border-bottom: 1px dashed var(--border-color);
}

.modal-group-order:last-child {
  border-bottom: none;
}

.modal-group-order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.2rem;
}

.modal-group-order-no {
  font-size: 0.75rem;
  color: var(--text-primary);
}

.modal-group-order-qty {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.modal-group-pallets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  padding-left: 0.5rem;
}

.modal-group-pallet-tag {
  font-size: 0.7rem;
  color: var(--text-secondary);
  background: var(--border-color);
  padding: 0.15rem 0.4rem;
  border-radius: 0.2rem;
}

.modal-order-item {
  padding: 0.5rem 0.3rem;
  border-bottom: 1px solid var(--border-color);
}

.modal-order-item:last-child {
  border-bottom: none;
}

.modal-order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.3rem;
  flex-wrap: wrap;
  gap: 0.2rem;
}

.modal-order-no {
  font-size: 0.8rem;
  color: var(--text-primary);
  font-weight: 500;
}

.modal-order-material {
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-left: 0.3rem;
}

.modal-pallet-count {
  font-size: 0.75rem;
  color: var(--primary-color);
}

.modal-pallet-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.modal-pallet-tag {
  font-size: 0.7rem;
  background: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
  padding: 0.2rem 0.5rem;
  border-radius: 0.3rem;
}

.modal-footer {
  display: flex;
  border-top: 1px solid var(--border-color);
}

.cancel-btn {
  flex: 1;
  background: var(--background-color);
  color: var(--text-primary);
  border-radius: 0;
  border-right: 1px solid var(--border-color);
}

.confirm-modal-btn {
  flex: 1;
  background: var(--success-color);
  color: var(--primary-text);
  border-radius: 0;
}
</style>
